import {
  browserLocalPersistence,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithPopup,
  setPersistence,
  signInWithRedirect,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";
import { initializeApp } from "firebase/app";

import "./cloud-shell.css";
import { installAccountMenu } from "./cloud/account-menu";
import {
  FirebaseCloudApi,
  type CloudAssetUpload,
  type CloudInvite,
} from "./cloud/cloud-api";
import { CloudRepository } from "./cloud/cloud-repository";
import { IndexedDbCloudCache } from "./cloud/indexeddb-cache";
import {
  clearLegacyDeviceCache,
  readLegacyState,
  writeLegacyState,
} from "./cloud/legacy-storage";
import type { LegacyState } from "./cloud/state-codec";

type LegacyCloudService = Readonly<{
  snapshot(): LegacyState;
  apply(state: LegacyState): Promise<boolean>;
  openEncryptedBackup(): void;
  hasSafetyBackup(): boolean;
  syncAssets(): Promise<Readonly<{ uploaded: number; failed: number }>>;
}>;

type LegacyRegistry = Readonly<{
  services?: Readonly<{ cloud?: LegacyCloudService }>;
}>;

type CloudBridge = {
  enabled: boolean;
  applyingRemote: boolean;
  onLocalCommit(state: LegacyState, serialized: string): void;
  uploadAsset(input: CloudAssetUpload): Promise<void>;
  downloadAsset(assetId: string): Promise<Blob>;
  deleteAsset(assetId: string): Promise<void>;
  restoreAsset(assetId: string): Promise<void>;
};

declare global {
  interface Window {
    SB?: LegacyRegistry;
    SecondBrainCloudBridge?: CloudBridge;
  }
}

type PublicConfig = Readonly<{
  firebase: Readonly<{
    apiKey: string;
    authDomain: string;
    projectId: string;
    appId: string;
  }>;
  apiBaseUrl: string;
}>;

function publicConfig(): PublicConfig {
  const values = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
  };
  const missing = Object.entries(values)
    .filter(([, value]) => typeof value !== "string" || value.length === 0)
    .map(([key]) => key);
  if (missing.length > 0) {
    throw new Error(`Public deployment configuration is incomplete: ${missing.join(", ")}`);
  }
  return {
    firebase: {
      apiKey: values.apiKey,
      authDomain: values.authDomain,
      projectId: values.projectId,
      appId: values.appId,
    },
    apiBaseUrl: values.apiBaseUrl,
  };
}

const gate = document.createElement("div");
gate.className = "cloud-gate";
gate.setAttribute("role", "dialog");
gate.setAttribute("aria-modal", "true");
document.body.append(gate);

let activeRepository: CloudRepository | null = null;
let activeApi: FirebaseCloudApi | null = null;
let activeUser: User | null = null;
let startupLocalState: LegacyState | null = null;
let legacyLoaded = false;
let syncTimer: number | null = null;
let accountBar: HTMLElement | null = null;
let accountBarAbortController: AbortController | null = null;
let eventSocket: WebSocket | null = null;
let eventRetryTimer: number | null = null;
let eventRetryAttempt = 0;

function clear(element: Element): void {
  while (element.firstChild !== null) element.firstChild.remove();
}

function button(label: string, primary = false): HTMLButtonElement {
  const control = document.createElement("button");
  control.type = "button";
  control.className = `cloud-button${primary ? " primary" : ""}`;
  control.textContent = label;
  return control;
}

function card(title: string, description: string): HTMLElement {
  clear(gate);
  gate.hidden = false;
  const panel = document.createElement("section");
  panel.className = "cloud-card";
  const eyebrow = document.createElement("div");
  eyebrow.className = "cloud-eyebrow";
  eyebrow.textContent = "Second Brain OS";
  const heading = document.createElement("h1");
  heading.textContent = title;
  const copy = document.createElement("p");
  copy.textContent = description;
  panel.append(eyebrow, heading, copy);
  gate.append(panel);
  return panel;
}

function status(panel: HTMLElement, message = ""): HTMLElement {
  const output = document.createElement("p");
  output.className = "cloud-status";
  output.setAttribute("role", "status");
  output.textContent = message;
  panel.append(output);
  return output;
}

function renderFailure(error: unknown): void {
  const panel = card(
    "Cloud connection needs attention",
    "Your local data has not been removed. Check the deployment configuration or connection and try again.",
  );
  const output = status(panel, error instanceof Error ? error.message : "Unexpected error");
  output.classList.add("error");
  const actions = document.createElement("div");
  actions.className = "cloud-actions";
  const retry = button("Retry", true);
  retry.addEventListener("click", () => location.reload());
  actions.append(retry);
  panel.append(actions);
}

function stateSummary(state: LegacyState): Readonly<{
  records: number;
  files: number;
  photos: number;
}> {
  let records = 0;
  for (const value of Object.values(state)) if (Array.isArray(value)) records += value.length;
  const attachments = Array.isArray(state.taskAttachments)
    ? state.taskAttachments.filter((value) => typeof value === "object" && value !== null).length
    : 0;
  const photos = Array.isArray(state.progressPhotos) ? state.progressPhotos.length : 0;
  return { records, files: attachments, photos };
}

function hasMeaningfulLocalData(state: LegacyState | null): state is LegacyState {
  if (state === null) return false;
  const summary = stateSummary(state);
  if (summary.records > 0 || summary.files > 0 || summary.photos > 0) return true;
  return (
    typeof state.settings === "object" &&
    state.settings !== null &&
    Reflect.get(state.settings, "onboarded") === true
  );
}

function summaryMarkup(panel: HTMLElement, state: LegacyState): void {
  const summary = stateSummary(state);
  const grid = document.createElement("div");
  grid.className = "cloud-summary";
  for (const [label, value] of [
    ["Structured records", summary.records],
    ["Task files", summary.files],
    ["Progress photos", summary.photos],
  ] as const) {
    const item = document.createElement("div");
    const amount = document.createElement("b");
    amount.textContent = value.toLocaleString();
    const caption = document.createElement("span");
    caption.textContent = label;
    item.append(amount, caption);
    grid.append(item);
  }
  panel.append(grid);
}

async function loadScript(source: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = source;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error(`Could not load ${source}`)), {
      once: true,
    });
    document.body.append(script);
  });
}

async function loadLegacyApplication(): Promise<LegacyCloudService> {
  if (!legacyLoaded) {
    await loadScript("/legacy-bootstrap.js");
    await loadScript("/legacy-app.js");
    legacyLoaded = true;
  }
  const cloud = window.SB?.services?.cloud;
  if (cloud === undefined) throw new Error("Legacy cloud compatibility service is unavailable");
  return cloud;
}

function setSyncBusy(busy: boolean): void {
  accountBar?.classList.toggle("sync-busy", busy);
  accountBar?.querySelector(".cloud-sync-dot")?.classList.toggle("busy", busy);
}

function stopInvalidationSocket(): void {
  if (eventRetryTimer !== null) window.clearTimeout(eventRetryTimer);
  eventRetryTimer = null;
  eventRetryAttempt = 0;
  const socket = eventSocket;
  eventSocket = null;
  socket?.close(1000, "Session ended");
}

function connectInvalidationSocket(user: User, api: FirebaseCloudApi): void {
  if (activeUser?.uid !== user.uid || !navigator.onLine) return;
  void user
    .getIdToken()
    .then((token) => {
      if (activeUser?.uid !== user.uid) return;
      eventSocket?.close(1000, "Replacing connection");
      const socket = new WebSocket(api.eventSocketUrl(), ["firebase-auth", token]);
      eventSocket = socket;
      socket.addEventListener("open", () => {
        eventRetryAttempt = 0;
      });
      socket.addEventListener("message", (event) => {
        if (event.data === "pong") return;
        try {
          const message: unknown = JSON.parse(String(event.data));
          if (
            typeof message === "object" &&
            message !== null &&
            Reflect.get(message, "type") === "revision"
          ) {
            void reconcileIntoRunningApp();
          }
        } catch {
          socket.close(1003, "Invalid invalidation message");
        }
      });
      socket.addEventListener("close", () => {
        if (eventSocket === socket) eventSocket = null;
        if (activeUser?.uid !== user.uid) return;
        const delay = Math.min(60_000, 1_000 * 2 ** eventRetryAttempt);
        eventRetryAttempt += 1;
        eventRetryTimer = window.setTimeout(() => connectInvalidationSocket(user, api), delay);
      });
    })
    .catch(() => undefined);
}

// Focus and visibilitychange both fire on a single tab switch, so an
// unguarded reconcile ran the whole sync/apply cycle twice. Callers join the
// in-flight pass instead of starting a competing one.
let reconcileInFlight: Promise<void> | null = null;

async function reconcileIntoRunningApp(): Promise<void> {
  if (activeRepository === null || !legacyLoaded || !navigator.onLine) return;
  if (reconcileInFlight !== null) return reconcileInFlight;
  const pass = (async () => {
    setSyncBusy(true);
    try {
      const state = await activeRepository.syncNow();
      await window.SB?.services?.cloud?.apply(state);
    } finally {
      setSyncBusy(false);
    }
  })();
  reconcileInFlight = pass.finally(() => {
    reconcileInFlight = null;
  });
  return reconcileInFlight;
}

function installBridge(repository: CloudRepository, api: FirebaseCloudApi): void {
  const bridge: CloudBridge = {
    enabled: false,
    applyingRemote: false,
    onLocalCommit(state) {
      if (!bridge.enabled || bridge.applyingRemote) return;
      if (syncTimer !== null) window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(() => {
        syncTimer = null;
        setSyncBusy(true);
        repository
          .queueState(state)
          .then(() => (navigator.onLine ? repository.syncNow() : state))
          .catch(() => state)
          .finally(() => setSyncBusy(false));
      }, 600);
    },
    async uploadAsset(input) {
      await api.uploadAsset(input);
    },
    downloadAsset(assetId) {
      return api.downloadAsset(assetId);
    },
    deleteAsset(assetId) {
      return api.deleteAsset(assetId);
    },
    restoreAsset(assetId) {
      return api.restoreAsset(assetId);
    },
  };
  window.SecondBrainCloudBridge = bridge;
}

function enableBridge(): void {
  if (window.SecondBrainCloudBridge !== undefined) {
    window.SecondBrainCloudBridge.enabled = true;
  }
}

function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

async function signInWithGoogle(auth: Auth): Promise<void> {
  await signInWithRedirect(auth, googleProvider());
}

async function confirmRecentGoogleSession(user: User): Promise<void> {
  await reauthenticateWithPopup(user, googleProvider());
}

function showDriveManager(user: User, api: FirebaseCloudApi): void {
  const panel = card(
    "Google Drive file storage",
    "Drive stores only app-managed photos, attachments, and encrypted backup packages. Structured records stay in your isolated cloud database.",
  );
  const output = status(panel, "Checking connection...");
  const actions = document.createElement("div");
  actions.className = "cloud-actions";
  panel.append(actions);
  void api
    .driveStatus()
    .then((drive) => {
      clear(actions);
      if (drive.connected) {
        output.textContent = "Connected to the visible Second Brain OS folder.";
        const disconnect = button("Disconnect Drive");
        disconnect.addEventListener("click", async () => {
          disconnect.disabled = true;
          output.textContent = "Confirming your Google account...";
          try {
            await confirmRecentGoogleSession(user);
            output.textContent = "Revoking Drive access...";
            await api.disconnectDrive();
            location.reload();
          } catch (error) {
            disconnect.disabled = false;
            output.textContent = error instanceof Error ? error.message : "Drive disconnection failed";
            output.classList.add("error");
          }
        });
        actions.append(disconnect);
      } else {
        output.textContent = "Not connected. The app will request only the drive.file scope.";
        const connect = button("Connect Google Drive", true);
        connect.addEventListener("click", async () => {
          connect.disabled = true;
          output.textContent = "Confirming your Google account...";
          try {
            await confirmRecentGoogleSession(user);
            location.assign(await api.startDriveOAuth());
          } catch (error) {
            connect.disabled = false;
            output.textContent = error instanceof Error ? error.message : "Drive connection failed";
            output.classList.add("error");
          }
        });
        actions.append(connect);
      }
      const close = button("Close");
      close.addEventListener("click", () => location.reload());
      actions.append(close);
    })
    .catch((error: unknown) => {
      output.textContent = error instanceof Error ? error.message : "Drive status failed";
      output.classList.add("error");
    });
}

function showAccountSwitch(user: User): void {
  const panel = card(
    "Switch Google account",
    "Pending changes will sync first. Device caches for the current account will then be cleared before Google asks you to choose another account.",
  );
  const output = status(panel);
  const actions = document.createElement("div");
  actions.className = "cloud-actions";
  const proceed = button("Sync and switch", true);
  proceed.addEventListener("click", async () => {
    if (activeRepository === null) return;
    proceed.disabled = true;
    output.textContent = "Synchronizing...";
    try {
      await activeRepository.clearForLogout();
      stopInvalidationSocket();
      await clearLegacyDeviceCache();
      startupLocalState = null;
      window.SecondBrainCloudBridge = undefined;
      await signOut(getAuth());
      await signInWithGoogle(getAuth());
      location.reload();
    } catch (error) {
      proceed.disabled = false;
      output.textContent = error instanceof Error ? error.message : "Account switch was cancelled";
      output.classList.add("error");
    }
  });
  const cancel = button("Cancel");
  cancel.addEventListener("click", () => location.reload());
  actions.append(proceed, cancel);
  panel.append(actions);
  void user;
}

async function ensureDailyCloudBackup(api: FirebaseCloudApi): Promise<void> {
  const backups = await api.listBackups();
  const newest = backups.reduce((latest, backup) => Math.max(latest, backup.createdAt), 0);
  if (Date.now() - newest >= 24 * 60 * 60_000) await api.createBackup("automatic");
}

function inviteLabel(invite: CloudInvite): string {
  const detail =
    invite.status === "pending"
      ? `expires ${new Date(invite.expiresAt).toLocaleDateString()}`
      : invite.status;
  return `${invite.email} - ${detail}`;
}

function showInviteManager(user: User, api: FirebaseCloudApi): void {
  const panel = card(
    "Invitations",
    "Only invited Google accounts can sign in. An invitation is claimed automatically the first time that account signs in.",
  );
  const output = status(panel, "Loading invitations...");
  const actions = document.createElement("div");
  actions.className = "cloud-actions vertical";
  panel.append(actions);

  const close = button("Close");
  close.addEventListener("click", () => location.reload());

  const renderList = (invites: CloudInvite[]): void => {
    clear(actions);
    output.classList.remove("error");
    output.textContent =
      invites.length === 0 ? "No invitations yet." : `${invites.length} invitation(s).`;

    const email = document.createElement("input");
    email.type = "email";
    email.className = "cloud-input";
    email.placeholder = "name@example.com";
    email.setAttribute("aria-label", "Google account email to invite");
    const days = document.createElement("input");
    days.type = "number";
    days.className = "cloud-input";
    days.min = "1";
    days.max = "30";
    days.value = "7";
    days.setAttribute("aria-label", "Invitation expires in days");

    const invite = button("Send invitation", true);
    invite.addEventListener("click", async () => {
      const address = email.value.trim().toLowerCase();
      const expiresInDays = Number(days.value);
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address)) {
        output.textContent = "Enter a valid email address.";
        output.classList.add("error");
        return;
      }
      if (!Number.isInteger(expiresInDays) || expiresInDays < 1 || expiresInDays > 30) {
        output.textContent = "Expiry must be between 1 and 30 days.";
        output.classList.add("error");
        return;
      }
      invite.disabled = true;
      output.classList.remove("error");
      output.textContent = "Confirming your Google account...";
      try {
        await confirmRecentGoogleSession(user);
        output.textContent = "Creating invitation...";
        await api.createInvite(address, expiresInDays);
        renderList(await api.listInvites());
        output.textContent = `Invited ${address}.`;
      } catch (error) {
        invite.disabled = false;
        output.textContent = error instanceof Error ? error.message : "Invitation failed";
        output.classList.add("error");
      }
    });

    actions.append(email, days, invite);

    for (const row of invites) {
      if (row.status !== "pending") {
        const settled = document.createElement("div");
        settled.className = "cloud-invite-row settled";
        const label = document.createElement("span");
        label.textContent = inviteLabel(row);
        settled.append(label);
        actions.append(settled);
        continue;
      }
      const line = document.createElement("div");
      line.className = "cloud-invite-row";
      const who = document.createElement("span");
      who.textContent = inviteLabel(row);
      const revoke = button("Revoke");
      revoke.classList.add("compact");
      revoke.addEventListener("click", async () => {
        revoke.disabled = true;
        output.classList.remove("error");
        output.textContent = "Confirming your Google account...";
        try {
          await confirmRecentGoogleSession(user);
          output.textContent = "Revoking invitation...";
          await api.revokeInvite(row.inviteId);
          renderList(await api.listInvites());
          output.textContent = `Revoked ${row.email}.`;
        } catch (error) {
          revoke.disabled = false;
          output.textContent = error instanceof Error ? error.message : "Revoke failed";
          output.classList.add("error");
        }
      });
      line.append(who, revoke);
      actions.append(line);
    }
    actions.append(close);
  };

  void api
    .listInvites()
    .then(renderList)
    .catch((error: unknown) => {
      clear(actions);
      output.textContent = error instanceof Error ? error.message : "Invitation listing failed";
      output.classList.add("error");
      actions.append(close);
    });
}

function showCloudRecovery(user: User, api: FirebaseCloudApi): void {
  const panel = card(
    "Cloud recovery snapshots",
    "Snapshots are checksum-verified structured records. Restoring creates a new safety snapshot first and does not replace Drive files.",
  );
  const output = status(panel, "Loading snapshots...");
  const actions = document.createElement("div");
  actions.className = "cloud-actions vertical";
  panel.append(actions);
  void api
    .listBackups()
    .then((backups) => {
      output.textContent = backups.length === 0 ? "No cloud snapshots yet." : "Choose a restore point.";
      for (const backup of backups) {
        const restore = button(
          `${new Date(backup.createdAt).toLocaleString()} - ${backup.kind.replace("_", " ")}`,
        );
        restore.addEventListener("click", () => {
          clear(actions);
          output.textContent = "This will restore structured records to the selected point. A pre-restore safety snapshot will be retained.";
          const confirm = button("Confirm restore", true);
          confirm.addEventListener("click", async () => {
            confirm.disabled = true;
            output.textContent = "Confirming your Google account...";
            try {
              await confirmRecentGoogleSession(user);
              output.textContent = "Restoring verified snapshot...";
              await api.restoreBackup(backup.backupId);
              location.reload();
            } catch (error) {
              confirm.disabled = false;
              output.textContent = error instanceof Error ? error.message : "Snapshot restore failed";
              output.classList.add("error");
            }
          });
          const cancel = button("Cancel");
          cancel.addEventListener("click", () => location.reload());
          actions.append(confirm, cancel);
        });
        actions.append(restore);
      }
      const close = button("Close");
      close.addEventListener("click", () => location.reload());
      actions.append(close);
    })
    .catch((error: unknown) => {
      output.textContent = error instanceof Error ? error.message : "Snapshot listing failed";
      output.classList.add("error");
    });
}

function createAccountBar(user: User, api: FirebaseCloudApi): void {
  accountBarAbortController?.abort();
  accountBar?.remove();
  const bar = document.createElement("aside");
  bar.className = "cloud-account-bar";
  bar.setAttribute("aria-label", "Cloud account and synchronization");
  const dot = document.createElement("i");
  dot.className = "cloud-sync-dot";
  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "cloud-account-trigger";
  trigger.textContent = "Cloud";
  trigger.setAttribute("aria-label", "Cloud account controls");
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-controls", "cloud-account-menu");
  const menu = document.createElement("div");
  menu.id = "cloud-account-menu";
  menu.className = "cloud-account-menu";
  const email = document.createElement("span");
  email.dataset.accountEmail = "";
  email.textContent = user.email ?? "Google account";
  const sync = document.createElement("button");
  sync.type = "button";
  sync.textContent = "Sync";
  sync.addEventListener("click", () => void reconcileIntoRunningApp());
  const drive = document.createElement("button");
  drive.type = "button";
  drive.textContent = "Drive";
  drive.addEventListener("click", () => showDriveManager(user, api));
  void api
    .driveStatus()
    .then((connection) => {
      drive.textContent = connection.connected ? "Drive OK" : "Connect Drive";
    })
    .catch(() => {
      drive.textContent = "Drive !";
    });
  const switcher = document.createElement("button");
  switcher.type = "button";
  switcher.textContent = "Switch";
  switcher.addEventListener("click", () => showAccountSwitch(user));
  const recovery = document.createElement("button");
  recovery.type = "button";
  recovery.textContent = "Recovery";
  recovery.addEventListener("click", () => showCloudRecovery(user, api));
  const invites = document.createElement("button");
  invites.type = "button";
  invites.textContent = "Invites";
  invites.addEventListener("click", () => showInviteManager(user, api));
  const logout = document.createElement("button");
  logout.type = "button";
  logout.textContent = "Log out";
  logout.addEventListener("click", () => void logoutCurrentAccount());
  menu.append(email, sync, drive, recovery, invites, switcher, logout);
  bar.append(dot, trigger, menu);
  document.body.append(bar);
  accountBar = bar;
  accountBarAbortController = installAccountMenu(bar, trigger);
  connectInvalidationSocket(user, api);
}

async function completeLocalClaim(
  repository: CloudRepository,
  cloud: LegacyCloudService,
): Promise<void> {
  const snapshot = cloud.snapshot();
  await repository.queueState(snapshot);
  await repository.syncNow();
  enableBridge();
  const summary = stateSummary(snapshot);
  if (summary.files > 0 || summary.photos > 0) {
    const drive = await activeApi?.driveStatus();
    if (drive?.connected !== true) {
      throw new Error("Connect Google Drive before migrating photos and attachments.");
    }
    const assetResult = await cloud.syncAssets();
    if (assetResult.failed > 0) {
      throw new Error(`${assetResult.failed} file upload(s) are still pending. Retry after checking Drive.`);
    }
  }
  await repository.markClaimCompleted();
  gate.hidden = true;
}

function showMigrationReturn(reopen: () => void): void {
  const existing = document.querySelector<HTMLButtonElement>(".cloud-return");
  existing?.remove();
  const control = button("Return to cloud migration", true);
  control.classList.add("cloud-return");
  control.addEventListener("click", () => {
    control.remove();
    reopen();
  });
  document.body.append(control);
}

function migrationWizard(
  local: LegacyState,
  cloudState: LegacyState,
  repository: CloudRepository,
  cloud: LegacyCloudService,
  cloudExists: boolean,
): void {
  const reopen = () => migrationWizard(local, cloudState, repository, cloud, cloudExists);
  const panel = card(
    cloudExists ? "Review the two workspaces" : "Claim this local workspace",
    cloudExists
      ? "Cloud data already exists for this Google account. Nothing will be replaced automatically."
      : "Review the local data attached to this browser before uploading it to your private cloud workspace.",
  );
  summaryMarkup(panel, local);
  const safety = document.createElement("p");
  safety.className = "cloud-safety";
  safety.textContent = cloud.hasSafetyBackup()
    ? "Encrypted safety backup confirmed on this device."
    : "Required first: create and download the full encrypted .sbrain safety backup.";
  panel.append(safety);
  const output = status(panel);
  const actions = document.createElement("div");
  actions.className = "cloud-actions";
  const backup = button("Create encrypted safety backup");
  backup.addEventListener("click", () => {
    gate.hidden = true;
    cloud.openEncryptedBackup();
    showMigrationReturn(reopen);
  });
  const confirm = button(cloudExists ? "Merge safely" : "Upload and claim", true);
  confirm.addEventListener("click", async () => {
    if (!cloud.hasSafetyBackup()) {
      output.textContent = "Create the encrypted safety backup before continuing.";
      output.classList.add("error");
      return;
    }
    confirm.disabled = true;
    output.classList.remove("error");
    output.textContent = "Verifying and synchronizing…";
    try {
      if (cloudExists) await cloud.apply(cloudState);
      await completeLocalClaim(repository, cloud);
    } catch (error) {
      confirm.disabled = false;
      output.textContent = error instanceof Error ? error.message : "Migration failed";
      output.classList.add("error");
    }
  });
  actions.append(backup, confirm);
  if (cloudExists) {
    const useCloud = button("Use cloud workspace");
    useCloud.addEventListener("click", async () => {
      if (!cloud.hasSafetyBackup()) {
        output.textContent = "Create the encrypted safety backup before continuing.";
        output.classList.add("error");
        return;
      }
      await writeLegacyState(cloudState);
      await repository.markClaimCompleted();
      location.reload();
    });
    actions.append(useCloud);
  }
  panel.append(actions);
}

async function startAuthenticatedSession(user: User): Promise<void> {
  activeUser = user;
  const config = publicConfig();
  const api = new FirebaseCloudApi(config.apiBaseUrl, () => user.getIdToken());
  activeApi = api;
  const repository = new CloudRepository(user.uid, api, new IndexedDbCloudCache());
  activeRepository = repository;
  installBridge(repository, api);

  let cloudState: LegacyState;
  let online = true;
  try {
    cloudState = await repository.reconcile();
  } catch (error) {
    online = false;
    if (!(await repository.hasCompletedClaim())) throw error;
    cloudState = await repository.cachedState();
  }
  const cloudExists = await repository.hasCloudRecords();
  const claimed = await repository.hasCompletedClaim();
  const local = startupLocalState;

  if (!claimed && hasMeaningfulLocalData(local)) {
    await writeLegacyState(local);
    const cloud = await loadLegacyApplication();
    createAccountBar(user, api);
    migrationWizard(local, cloudState, repository, cloud, cloudExists);
    return;
  }

  await writeLegacyState(cloudExists ? cloudState : local ?? cloudState);
  await loadLegacyApplication();
  await repository.markClaimCompleted();
  enableBridge();
  createAccountBar(user, api);
  gate.hidden = true;
  if (!online) setSyncBusy(true);
  if (online) {
    void ensureDailyCloudBackup(api).catch(() => undefined);
    void api
      .driveStatus()
      .then((drive) => (drive.connected ? window.SB?.services?.cloud?.syncAssets() : undefined))
      .catch(() => undefined);
  }
}

async function logoutCurrentAccount(): Promise<void> {
  if (activeRepository === null || activeUser === null) return;
  const panel = card("Signing out safely", "Finishing any pending cloud changes first.");
  const output = status(panel, "Synchronizing…");
  try {
    await activeRepository.clearForLogout();
    stopInvalidationSocket();
    await clearLegacyDeviceCache();
    startupLocalState = null;
    window.SecondBrainCloudBridge = undefined;
    await signOut(getAuth());
    location.reload();
  } catch (error) {
    output.textContent =
      error instanceof Error
        ? `Sign-out paused: ${error.message}`
        : "Sign-out paused because cloud changes are pending.";
    output.classList.add("error");
  }
}

async function showSignIn(): Promise<void> {
  const panel = card(
    "Your Second Brain, on every device",
    "Sign in with Google. Google verifies your identity; your structured records stay in your private Second Brain cloud workspace.",
  );
  const actions = document.createElement("div");
  actions.className = "cloud-actions";
  const signIn = button("Continue with Google", true);
  const output = status(panel);
  signIn.addEventListener("click", async () => {
    signIn.disabled = true;
    output.textContent = "Opening Google Sign-In…";
    try {
      await signInWithGoogle(getAuth());
    } catch (error) {
      signIn.disabled = false;
      output.textContent = error instanceof Error ? error.message : "Google Sign-In failed";
      output.classList.add("error");
    }
  });
  actions.append(signIn);
  panel.insertBefore(actions, output);
}

async function main(): Promise<void> {
  startupLocalState = await readLegacyState();
  const config = publicConfig();
  const app = initializeApp(config.firebase);
  const auth = getAuth(app);
  await setPersistence(auth, browserLocalPersistence);
  await getRedirectResult(auth);

  onAuthStateChanged(auth, (user) => {
    if (user === null) {
      void showSignIn();
      return;
    }
    void startAuthenticatedSession(user).catch(renderFailure);
  });

  window.addEventListener("online", () => {
    void reconcileIntoRunningApp();
    if (activeUser !== null && activeApi !== null && eventSocket === null) {
      connectInvalidationSocket(activeUser, activeApi);
    }
  });
  window.addEventListener("focus", () => void reconcileIntoRunningApp());
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void reconcileIntoRunningApp();
  });
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      void navigator.serviceWorker.register("/second-brain-sw.js");
    });
  }
}

void main().catch(renderFailure);
